import strings from "assets/strings";
import Text from "components/text/Text";
import { Doctorim, Shidurit } from "assets/images";
import { MashlimDoctorType } from "types";
import Popup from "components/popup/Popup";
import { grad1, grad2 } from "assets/constants";
import mergedSettings from "assets/mergeSettings";
import Button from "components/buttons/button/Button";
import styles from "./MashlimDoctorResult.module.scss";
import SvgGenerator from "components/svgGenerator/SvgGenerator";
import { getAllClassNames, formatDate, getDayOfWeek } from "helpers";
import React, { forwardRef, useRef, useState, useEffect } from "react";
import { CustomalertPopup } from "components/popup/customAlertPopup/CustomalertPopup";

interface MashlimDoctorResultProps {
  hit: MashlimDoctorType;
  tabIndex?: number;
}

const getSearchApiUrl = (): string => {
  try {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes("infodev")) {
      return "infodevsearchapi.leumit.co.il";
    }
    if (hostname.includes("infoqa")) {
      return "infoqasearchapi.leumit.co.il";
    }
    return "infosearchapi.leumit.co.il";
  } catch {
    return "infosearchapi.leumit.co.il";
  }
};

const SEARCH_API_URL = getSearchApiUrl();

const formatShiduritFirstSlot = (slot: string): string => {
  const trimmedSlot = slot?.trim();
  if (!trimmedSlot) {
    return "";
  }

  const slotMatch = trimmedSlot.match(/^(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2})(?::\d{2})?$/);
  if (!slotMatch) {
    return trimmedSlot;
  }

  const [, datePart, timePart] = slotMatch;
  return `${datePart} בשעה ${timePart}`;
};

export const MashlimDoctorResult = forwardRef<HTMLDivElement, MashlimDoctorResultProps>(
  ({ hit, tabIndex }, ref) => {

  const popupRef = useRef<HTMLDivElement>(null);
  const [shiduritFirstSlot, setShiduritFirstSlot] = useState<string>("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggleVisibility = () => {
    popupRef.current?.classList.toggle('show');
  };

  const onExternalAppointment = (hit: MashlimDoctorType) => {
    window.open(hit.externalUrl, "_blank");
  };

  const onNavigate = (hit: MashlimDoctorType) => {
    const { medicalCenter } = hit;
    const address = `${medicalCenter.street || ''} ${medicalCenter.houseNumber || ''} ${medicalCenter.city || ''}`;
    const encodedAddress = encodeURIComponent(address);
    const mobileUrl = `geo://0,0?q=${encodedAddress}`;
    const iosUrl = `maps://?q=${encodedAddress}`;
    const desktopUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    function isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
    function isIOS() {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
    const url = isMobileDevice() ? (isIOS() ? iosUrl : mobileUrl) : desktopUrl;
    window.open(url, "_blank");
  };

  const distanceMeters = (hit as any)?._rankingInfo?.geoDistance;
  const distanceKm =
    typeof distanceMeters === "number" ? (distanceMeters / 1000).toFixed(2) : null;

  const { firstName, lastName, title, medicalFieldName, firstAvailableAppointmentDate, externalUrl } = hit;
  const isShidurit = (hit as any).externalSource === "shidurit";

  useEffect(() => {
    const fetchShiduritFirstSlot = async () => {
      if (!isShidurit || !hit.licenseId) {
        setShiduritFirstSlot("");
        return;
      }

      try {
        const response = await fetch(
          `https://${SEARCH_API_URL}/api/search/shidurit/doctors/${hit.licenseId}/firstslot/auto-token`
        );

        if (!response.ok) {
          setShiduritFirstSlot("");
          return;
        }

        const data = await response.json();
        setShiduritFirstSlot(typeof data?.Slot === "string" ? data.Slot : "");
      } catch {
        setShiduritFirstSlot("");
      }
    };

    fetchShiduritFirstSlot();
  }, [isShidurit, hit.licenseId]);

  const doctorHref = `${
    hit.objectType === 'Doctor'
      ? mergedSettings.doctorPage
      : hit.objectType === 'Diet'
        ? mergedSettings.dietPage
        : mergedSettings.socialWorkerPage
  }${hit.licenseId ? `?licenseId=${hit.licenseId}` : ''
  }${hit.medicalFieldFieldCode ? `${hit.licenseId ? '&' : '?'}code=${hit.medicalFieldFieldCode}` : ''
  }${hit.doctorIdentity ? `${hit.licenseId ? '&' : '?'}did=${hit.doctorIdentity}` : ''
  }${hit.externalDoctorType ? `${hit.licenseId || hit.medicalFieldFieldCode ? '&' : '?'}doctorType=${hit.externalDoctorType}` : ''
  }`;

  const defaultFirstSlotText = `${getDayOfWeek(String(firstAvailableAppointmentDate))} ${formatDate(String(firstAvailableAppointmentDate))}`;
  const rawFirstSlotText = isShidurit ? formatShiduritFirstSlot(shiduritFirstSlot) : defaultFirstSlotText;
  const firstSlotText = rawFirstSlotText?.trim() || "לא זמין כרגע";

  const textLine1Desktop = `בחרת לזמן תור ל${title} ${firstName} ${lastName} בתחום ${medicalFieldName}`;
  const textLine1Desktop2 = `התור הזמין הקרוב ביותר: ${firstSlotText}`;

  const textLine1Mobile = `בחרת לזמן תור ל${title} ${firstName} ${lastName} בתחום ${medicalFieldName}`;
  const textLine1Mobile2 = `התור הזמין הקרוב ביותר: <div>${firstSlotText}</div>`;

  let tooltipContentFirst = strings.doctorSearchResults.doctorimPopupContent;
  let tooltipContentSecond = '';
  if (strings.doctorSearchResults.doctorimPopupContent.length > 200) {
    tooltipContentFirst = strings.doctorSearchResults.doctorimPopupContent.substring(0, 200) + '...';
    tooltipContentSecond = strings.doctorSearchResults.doctorimPopupContent.substring(200);
  }

  const appointmentSystemLogo = isShidurit ? Shidurit : Doctorim;
  const appointmentSystemAlt = isShidurit ? "שידורית" : "דוקתורים";

  return (
    <div ref={ref} tabIndex={tabIndex}>
      <div className={styles.hitDoctor}>

        <div className={styles.section2}>
          {hit.gender === "נ" ? (
            <SvgGenerator name="Female" className={styles.avatar} objectName="doctorSearchResults" />
          ) : (
            <SvgGenerator name="Male" className={styles.avatar} objectName="doctorSearchResults" />
          )}
          <div className={styles.divText}>
            <Text
              className={styles.textGray}
              text={hit.externalDoctorType === 1 ? strings.doctorSearchResults.doctorArrange : strings.doctorSearchResults.doctorRefund}
            />
            <a href={doctorHref} className={styles.textTitle}>
              {`${hit.title} ${hit.firstName} ${hit.lastName}`}
            </a>
<Text className={styles.textPurple} text={`${hit.medicalFieldName ? hit.medicalFieldName : ''} ${hit.subMedicalFieldNames ? ` | ${hit.subMedicalFieldNames.join(",")}` : ''} `} />            <Text
              className={styles.text}
              text={`${strings.doctorSearchResults.licenseNum} ${hit.licenseId}`}
            />
          </div>
        </div>

        {hit.medicalCenter.city && (
          <div className={styles.section}>
            <div className={styles.divText}>
              <Text className={styles.textTitle2} text={strings.globalStrings.address} />
              <span
                id={`navigateToAddress_${hit.objectID}`}
                className={styles.textAddress}>
                {`${hit.medicalCenter.city}${hit.medicalCenter.street ? ',' : ''} ${hit.medicalCenter.street} ${hit.medicalCenter.houseNumber}`}
              </span>
              {hit._geoloc && (
                <span onClick={() => onNavigate(hit)} className={styles.span} aria-describedby={`navigateToAddress_${hit.objectID}`} tabIndex={0} role="link">
                  <SvgGenerator name="Location" className={styles.icon} objectName="doctorSearchResults" />
                  <Text className={styles.textNavigate} text={strings.doctorSearchResults.navigateToAddress} />
                </span>
              )}
            </div>
          </div>
        )}

        <div className={styles.section3}>
          <div className={styles.divText2}>
            {(externalUrl && externalUrl !== '') ? (
              <>
                <Button
                  className={styles.OrderButton}
                  color1={grad1}
                  color2={grad2}
                  onClick={() => handleToggleVisibility()}
                  text={strings.doctorSearchResults.getAppointment}
                />
                <span
                 className={getAllClassNames([
                    styles.span,
                    styles.marginTop,
                    styles.appointmentSystemRow,
                    styles.appointmentSystemUnderButton
                    ])}>
                  <Text className={styles.textDoctorim} text={strings.doctorSearchResults.appointmentSystem} />
                  <img src={appointmentSystemLogo} alt={appointmentSystemAlt} className={styles.appointmentSystemLogo}/> 
                </span>
              </>
            ) : (
              <>
                <Text className={styles.text} text={strings.doctorSearchResults.callToOrder} />
                {hit.medicalCenter.phone1 && <Text className={styles.textBold} text={hit.medicalCenter.phone1} />}
                {hit.medicalCenter.phone2 && <Text className={styles.textBold} text={hit.medicalCenter.phone2} />}
              </>
            )}
          </div>
          {distanceKm !== null && (
            <div className={styles.distanceSection}>
              <Text className={styles.textTitle2} text={`${distanceKm} ק"מ`} />
            </div>
          )}
        </div>

      </div>

      {hit.externalDoctorType === 1 && (
        <div className={styles.divBottom}>
          <SvgGenerator name="Tag" className={styles.icon} objectName="doctorSearchResults" />
          <Text
            className={styles.text}
            text={`${strings.doctorSearchResults.selfPay1} ${hit.paymentSum} ${strings.doctorSearchResults.selfPay2}`}
          />
        </div>
      )}

      <Popup
        ref={popupRef}
        style={{ padding: '25px', height: 'auto' }}
        height={'auto'}
        width={690}
        onClose={() => handleToggleVisibility()}>
        <CustomalertPopup
          title={isShidurit ? "יש לזמן תור דרך מערכת Rofim.Org" : strings.doctorSearchResults.doctorimPopupTitle}
          titeStyle={{ padding: '7px 0 17px' }}
          buttonText={strings.doctorSearchResults.getAppointment}
          onClose={() => onExternalAppointment(hit)}
          showButton
          paragraphs={[
            { text: isMobile ? textLine1Mobile : textLine1Desktop, style: { background: '#F6F1F6', whiteSpace: 'pre-line', padding: '1rem', width: '100%' }, isHtml: isMobile },
            { text: isMobile ? textLine1Mobile2 : textLine1Desktop2, style: { background: '#F6F1F6', whiteSpace: 'pre-line', padding: '0 1rem 1rem', width: '100%' }, isHtml: isMobile },
            { text: tooltipContentFirst, style: { whiteSpace: 'pre-line', lineHeight: 1.5 } },
          ]}
          toggledTextExpand={strings.globalStrings.expand}
          toggledTextCollapse={strings.globalStrings.collapse}
          toggledTextTitleStyle={{ color: '#2E567A', fontSize: 16 }}
          toggledText={tooltipContentSecond}
        />
      </Popup>

    </div>
  );
});